import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui-bits";
import { Trash2, CheckCircle2, Circle, Lock, FileText, ListChecks, Paperclip, Plus, Loader2, Pencil, ArrowUp, ArrowDown, X, Save } from "lucide-react";
import { toast } from "sonner";

const CAT_COLOR = {
  Lettings: "bg-blue-100 text-blue-700",
  Refurbishment: "bg-amber-100 text-amber-700",
  Compliance: "bg-purple-100 text-purple-700",
  Sales: "bg-emerald-100 text-emerald-700",
  General: "bg-slate-100 text-slate-600",
};

const SUGGESTED_STAGES = [
  { name: "Survey & scope of works", checklist: ["Site visit booked", "Scope agreed with landlord"] },
  { name: "Budget & quotes", checklist: ["Quotes requested", "Budget approved"] },
  { name: "Strip out & clearance", checklist: ["Skip ordered", "Site cleared"] },
  { name: "Structural / damp works", checklist: [] },
  { name: "First fix (electrics & plumbing)", checklist: ["Electrics first fix", "Plumbing first fix"] },
  { name: "Plastering", checklist: [] },
  { name: "Second fix (electrics & plumbing)", checklist: [] },
  { name: "Kitchen installation", checklist: [] },
  { name: "Bathroom installation", checklist: [] },
  { name: "Flooring", checklist: [] },
  { name: "Decorating", checklist: [] },
  { name: "Snagging & final clean", checklist: ["Snag list completed", "Professional clean"] },
  { name: "Compliance certs (EPC / Gas / EICR)", checklist: ["EPC", "Gas safety", "EICR"] },
  { name: "Final inspection & sign-off", checklist: [] },
];
const SUGGESTED_NAMES = new Set(SUGGESTED_STAGES.map((s) => s.name));

export function WorkflowTracker({ workflowId, wf: wfProp, onChanged, onDeleted, readOnly = false, projectId = null }) {
  const [wf, setWf] = useState(wfProp || null);
  const [loading, setLoading] = useState(!wfProp && !!workflowId);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (workflowId) {
      setLoading(true);
      api.get(`/workflows/${workflowId}`).then(({ data }) => setWf(data)).catch(() => setWf(null)).finally(() => setLoading(false));
    }
  }, [workflowId]);

  const act = async (url) => {
    try { const { data } = await api.post(url); setWf(data.workflow); onChanged && onChanged(data.workflow); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const removeWf = async () => {
    if (!window.confirm(`Delete workflow "${wf.name}"?`)) return;
    try { await api.delete(`/workflows/${wf.workflow_id}`); toast.success("Workflow deleted"); onDeleted && onDeleted(); }
    catch { toast.error("Could not delete workflow"); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-400" data-testid="wf-tracker-loading">Loading workflow…</div>;
  if (!wf) {
    if (projectId && !readOnly) {
      return (
        <div data-testid="workflow-create">
          <div className="mb-4">
            <h2 className="text-xl font-display font-semibold text-slate-900">Add workflow stages</h2>
            <p className="text-sm text-slate-500 mt-0.5">This project has no workflow yet. Add stages below to create one — each stage assigns a task and unlocks the next when complete.</p>
          </div>
          <StageEditor wf={{ workflow_id: null, category: "Refurbishment", stages: [] }} projectId={projectId}
            onCancel={() => onDeleted && onDeleted()}
            onSaved={(newWf) => { setWf(newWf); onChanged && onChanged(newWf); }} />
        </div>
      );
    }
    return <div className="p-6 text-sm text-slate-400">No workflow found.</div>;
  }
  const done = wf.stages.filter((s) => s.status === "Complete").length;

  return (
    <div data-testid="workflow-tracker">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${CAT_COLOR[wf.category] || CAT_COLOR.General}`}>{wf.category}</span>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${wf.status === "Completed" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{wf.status}</span>
          </div>
          <h2 className="text-xl font-display font-semibold text-slate-900">{wf.name}</h2>
          {wf.linked_ref && <p className="text-sm text-blue-700 font-mono">{wf.linked_ref}</p>}
          <p className="text-xs text-slate-400 mt-1">{done}/{wf.stages.length} stages complete</p>
        </div>
        {!editing && !readOnly && (
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(true)} data-testid="wf-edit-stages" className="border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-1.5"><Pencil size={14} /> Edit stages</button>
            <button onClick={removeWf} data-testid="wf-delete" className="border border-red-200 text-red-600 hover:bg-red-50 rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-1.5"><Trash2 size={14} /> Delete</button>
          </div>
        )}
      </div>
      {editing ? (
        <StageEditor wf={wf} onCancel={() => setEditing(false)}
          onSaved={(newWf) => { setWf(newWf); setEditing(false); onChanged && onChanged(newWf); }} />
      ) : (
        <div className="space-y-4">
          {wf.stages.map((s, i) => <StageCard key={s.key} wf={wf} stage={s} index={i} onAct={act} readOnly={readOnly} />)}
        </div>
      )}
    </div>
  );
}

// ---------------- Stage editor (add / remove / reorder / multi-select) ----------------
function StageEditor({ wf, onCancel, onSaved, projectId = null }) {
  const [stages, setStages] = useState(() => wf.stages.map((s) => ({
    key: s.key, name: s.name, assignee: s.assignee || "", sop_text: s.sop_text || "",
    checklist: (s.checklist || []).map((c) => ({ item_id: c.item_id, text: c.text })),
    status: s.status, complete: s.status === "Complete",
  })));
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  const isRefurb = wf.category === "Refurbishment";

  useEffect(() => { api.get("/team-members").then(({ data }) => setStaff(data)).catch(() => {}); }, []);

  const update = (i, key, val) => setStages((ss) => ss.map((s, idx) => idx === i ? { ...s, [key]: val } : s));
  const move = (i, dir) => setStages((ss) => {
    const j = i + dir;
    if (j < 0 || j >= ss.length) return ss;
    const copy = [...ss];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });
  const addStage = () => setStages((ss) => [...ss, { name: "", assignee: "", sop_text: "", checklist: [{ text: "" }], complete: false }]);
  const removeStage = (i) => setStages((ss) => ss.filter((_, idx) => idx !== i));
  const addCheck = (si) => setStages((ss) => ss.map((s, i) => i === si ? { ...s, checklist: [...s.checklist, { text: "" }] } : s));
  const updateCheck = (si, ci, val) => setStages((ss) => ss.map((s, i) => i === si ? { ...s, checklist: s.checklist.map((c, j) => j === ci ? { ...c, text: val } : c) } : s));
  const removeCheck = (si, ci) => setStages((ss) => ss.map((s, i) => i === si ? { ...s, checklist: s.checklist.filter((_, j) => j !== ci) } : s));

  const isSuggested = (name) => stages.some((s) => s.name === name);
  const toggleSuggested = (sug) => setStages((ss) => {
    const idx = ss.findIndex((s) => s.name === sug.name);
    if (idx >= 0) return ss.filter((_, i) => i !== idx);
    return [...ss, { name: sug.name, assignee: "", sop_text: "", checklist: (sug.checklist.length ? sug.checklist : [""]).map((t) => ({ text: t })), complete: false }];
  });

  const save = async () => {
    const payload = stages
      .filter((s) => s.name.trim())
      .map((s) => ({
        key: s.key || undefined,
        name: s.name.trim(),
        assignee: s.assignee.trim(),
        sop_text: s.sop_text.trim(),
        checklist: s.checklist.map((c) => ({ item_id: c.item_id || undefined, text: (c.text || "").trim() })).filter((c) => c.text),
      }));
    if (!payload.length) { toast.error("Add at least one stage"); return; }
    setBusy(true);
    try {
      const { data } = wf.workflow_id
        ? await api.put(`/workflows/${wf.workflow_id}/stages`, { stages: payload })
        : await api.post(`/projects/${projectId}/workflow`, { stages: payload });
      toast.success(wf.workflow_id ? "Stages updated" : "Workflow created");
      onSaved(data.workflow);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save stages"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="stage-editor">
      {isRefurb && (
        <div className="rounded-lg border border-blue-200 bg-blue-50/70 p-3" data-testid="wf-suggested-stages">
          <p className="text-[11px] font-semibold text-blue-800 uppercase tracking-wider mb-2">Suggested stages — tick to add</p>
          <div className="flex flex-wrap gap-1.5">
            {SUGGESTED_STAGES.map((sug) => {
              const on = isSuggested(sug.name);
              return (
                <button type="button" key={sug.name} data-testid={`wf-suggest-${sug.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} onClick={() => toggleSuggested(sug)}
                  className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${on ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"}`}>
                  {on ? "✓ " : "+ "}{sug.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {stages.length === 0 && <p className="text-xs text-slate-400 text-center py-2">Add a stage below{isRefurb ? " or tick a suggestion above" : ""}.</p>}

      {stages.map((s, si) => (
        <div key={s.key || `new-${si}`} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid={`edit-stage-${si}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[11px] font-bold text-slate-500">{si + 1}</span>
              <span className="text-xs font-semibold text-slate-500">Stage {si + 1}</span>
              {s.complete && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Complete</span>}
            </div>
            <div className="flex items-center gap-1">
              <button type="button" data-testid={`move-up-${si}`} disabled={si === 0} onClick={() => move(si, -1)} className="text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed p-1"><ArrowUp size={15} /></button>
              <button type="button" data-testid={`move-down-${si}`} disabled={si === stages.length - 1} onClick={() => move(si, 1)} className="text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed p-1"><ArrowDown size={15} /></button>
              <button type="button" data-testid={`edit-remove-stage-${si}`} onClick={() => removeStage(si)} className="text-slate-300 hover:text-red-500 p-1"><Trash2 size={14} /></button>
            </div>
          </div>
          <input data-testid={`edit-stage-name-${si}`} value={s.name} onChange={(e) => update(si, "name", e.target.value)} placeholder="Stage name" className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
          <select data-testid={`edit-stage-assignee-${si}`} value={s.assignee} onChange={(e) => update(si, "assignee", e.target.value)} className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600">
            <option value="">Assign to (auto-creates their task)…</option>
            {staff.map((m) => <option key={m.user_id} value={m.name}>{m.name}</option>)}
            {s.assignee && !staff.some((m) => m.name === s.assignee) && <option value={s.assignee}>{s.assignee}</option>}
          </select>
          <textarea data-testid={`edit-stage-sop-${si}`} rows={2} value={s.sop_text} onChange={(e) => update(si, "sop_text", e.target.value)} placeholder="SOP — instructions for this stage (optional)" className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
          <div className="space-y-1.5">
            <label className="block text-[11px] font-medium text-slate-500">Checklist items</label>
            {s.checklist.map((c, ci) => (
              <div key={ci} className="flex items-center gap-2">
                <input data-testid={`edit-stage-${si}-check-${ci}`} value={c.text} onChange={(e) => updateCheck(si, ci, e.target.value)} placeholder="Add another item to this stage" className="flex-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <button type="button" onClick={() => removeCheck(si, ci)} className="text-slate-300 hover:text-red-500"><X size={13} /></button>
              </div>
            ))}
            <button type="button" data-testid={`edit-add-check-${si}`} onClick={() => addCheck(si)} className="text-xs font-medium text-blue-700 hover:text-blue-800">+ Add item</button>
          </div>
        </div>
      ))}

      <button type="button" data-testid="edit-add-stage-btn" onClick={addStage} className="w-full border border-dashed border-slate-300 text-slate-600 hover:border-blue-400 hover:text-blue-700 rounded-lg py-2 text-sm font-medium flex items-center justify-center gap-1.5"><Plus size={14} /> Add stage</button>

      <div className="flex items-center gap-2 pt-1">
        <button type="button" data-testid="wf-save-stages" onClick={save} disabled={busy} className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-1.5 disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save changes</button>
        <button type="button" data-testid="wf-cancel-edit" onClick={onCancel} className="border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg px-4 py-2 text-sm font-medium">Cancel</button>
      </div>
      <p className="text-[11px] text-slate-400">Reordering rebuilds the sequence — each stage unlocks only after the one above it is complete. Completed stages and their ticks are preserved.</p>
    </div>
  );
}

function StageCard({ wf, stage, index, onAct, readOnly = false }) {
  const locked = stage.status === "Locked";
  const complete = stage.status === "Complete";
  const base = `/workflows/${wf.workflow_id}/stages/${stage.key}`;
  const StatusIcon = complete ? CheckCircle2 : locked ? Lock : Circle;
  const iconColor = complete ? "text-emerald-600" : locked ? "text-slate-400" : "text-blue-600";

  return (
    <Card data-testid={`stage-${stage.key}`} className={`p-5 ${locked ? "opacity-70" : ""} ${complete ? "border-emerald-200 bg-emerald-50/30" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 shrink-0">{index + 1}</div>
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 flex items-center gap-2">{stage.name} <StatusIcon size={15} className={iconColor} /></p>
            <p className="text-xs text-slate-400">{stage.status}{locked && stage.depends_on.length ? " — complete prior stage(s) first" : ""}</p>
            {stage.assignee && (
              <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                <ListChecks size={11} className="text-blue-600" /> {stage.assignee}
                {stage.task_id && !locked && <span className="text-blue-600">· on their task list</span>}
              </p>
            )}
          </div>
        </div>
      </div>

      {stage.sop_text && (
        <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-3">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5"><FileText size={12} /> SOP — how to do this</p>
          <p className="text-sm text-slate-600 whitespace-pre-wrap">{stage.sop_text}</p>
        </div>
      )}

      {stage.checklist.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {stage.checklist.map((it) => (
            <button key={it.item_id} data-testid={`check-${it.item_id}`} disabled={readOnly || locked || complete}
              onClick={() => onAct(`${base}/checklist/${it.item_id}/toggle`)}
              className="flex items-center gap-2 text-left w-full disabled:cursor-not-allowed">
              {it.done ? <CheckCircle2 size={16} className="text-emerald-600 shrink-0" /> : <Circle size={16} className="text-slate-300 shrink-0" />}
              <span className={`text-sm ${it.done ? "text-slate-400 line-through" : "text-slate-700"}`}>{it.text}</span>
            </button>
          ))}
        </div>
      )}

      {!readOnly && !locked && <StageAttachments stageKey={stage.key} />}

      {!readOnly && !locked && (
        <div className="mt-4 flex gap-2">
          {complete ? (
            <button data-testid={`reopen-${stage.key}`} onClick={() => onAct(`${base}/reopen`)} className="border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg px-3 py-1.5 text-xs font-medium">Reopen stage</button>
          ) : (
            <button data-testid={`complete-${stage.key}`} onClick={() => onAct(`${base}/complete`)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-4 py-1.5 text-xs font-semibold flex items-center gap-1.5"><CheckCircle2 size={14} /> Mark stage complete</button>
          )}
        </div>
      )}
    </Card>
  );
}

function StageAttachments({ stageKey }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/files?entity_type=workflow_stage&entity_id=${stageKey}&bucket=documents`).then(({ data }) => setFiles(data)).catch(() => {});
  useEffect(() => { load(); }, [stageKey]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("entity_type", "workflow_stage");
    fd.append("entity_id", stageKey);
    fd.append("bucket", "documents");
    fd.append("file", file);
    try { await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Attached"); load(); }
    catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-2">
        {files.map((f) => (
          <a key={f.file_id} href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" data-testid={`stage-file-${f.file_id}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 hover:border-blue-300">
            <Paperclip size={12} /> {f.original_filename}
          </a>
        ))}
        <label className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-slate-300 px-2 py-1 text-xs text-slate-500 hover:bg-slate-50 cursor-pointer" data-testid={`stage-upload-${stageKey}`}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} SOP file
          <input type="file" className="hidden" onChange={upload} disabled={busy} />
        </label>
      </div>
    </div>
  );
}
